/**
 * Bundled Sauce Ultra Card — featured fallback until CMS publishes a
 * thumbnail + article-image card and marks it featured.
 */
import {
  isVisibleOnSauce,
  pickJourneyFeatured,
  pickSharedFeatured,
  type JourneyArticleFields,
} from '@shared/imageArticle';

export { isImageCardArticle } from '@shared/imageArticle';

export const DEMO_ARTICLE_ID = 'demo-ultra-card-01';
export const ULTRA_THUMB_PATH = '/articles/swyg-ultra-thumbnail.png';
export const ULTRA_FULL_CARD_PATH = '/articles/swyg-ultra-full-card.png';
/** Bust PWA / CDN caches when the provided artwork is replaced. */
export const ULTRA_ASSET_VERSION = 'copycat-20260929-w2x';

export function ultraAsset(path: string): string {
  return `${path}?v=${ULTRA_ASSET_VERSION}`;
}

export const DEMO_ARTICLE_HTML = `
<p>The "8 glasses a day" rule is a great original template. Don't copy it blindly.</p>
<p>It does not account for how much you sweat, whether you worked out, what you ate (a salty lunch counts), or how much caffeine you drank. Humans are also poor at estimating how much water they actually need.</p>
<p>Activity level, weather, and diet all change the number. Measure your own intake for three days and find <em>your</em> number — not the copy-cat 1.5L.</p>
<p><strong>+ Customise your water intake.</strong></p>
`.trim();

export const DEMO_LEARNING_CONTENT = [
  {
    id: DEMO_ARTICLE_ID,
    headline: "The '8 Glasses A Day' Rule Is A Great Original Template. Don't Copy It Blindly.",
    subHeading: "Don't be a copy cat. Their number isn't your number.",
    bodyText: DEMO_ARTICLE_HTML,
    imageUrl: ultraAsset(ULTRA_FULL_CARD_PATH),
    thumbnailUrl: ultraAsset(ULTRA_THUMB_PATH),
    headerImage: ultraAsset(ULTRA_FULL_CARD_PATH),
    linkUrl: '',
    section: 'blogs',
    isFeatured: true,
    isPublished: true,
  },
];

type FeaturedFields = JourneyArticleFields & {
  id?: string;
  isFeatured?: boolean;
  imageUrl?: string | null;
  thumbnailUrl?: string | null;
  headerImage?: string | null;
};

export function isUltraCardItem(item?: FeaturedFields | null): boolean {
  if (!item) return false;
  if (item.id === DEMO_ARTICLE_ID) return true;
  const blob = `${item.thumbnailUrl || ''} ${item.imageUrl || ''} ${item.headerImage || ''}`;
  return blob.includes('swyg-ultra-thumbnail') || blob.includes('swyg-ultra-full-card');
}

/**
 * Featured slot follows the member's journey day: day 1 is the join day.
 * Later days in the sequence stay hidden. Earlier days stay on Sauce as
 * regular cards. If this day has no image card, the Copy Cat card is featured.
 */
export function withBundledUltraArticle<T extends FeaturedFields>(
  items: T[] | null | undefined,
  journeyDay = 1
): T[] {
  const demo = DEMO_LEARNING_CONTENT[0] as unknown as T;
  const list = (Array.isArray(items) ? items : []).filter(
    (item) => item.id !== DEMO_ARTICLE_ID && isVisibleOnSauce(item, journeyDay)
  );
  const featuredCms = pickJourneyFeatured(list, journeyDay);
  const featured = featuredCms || demo;
  const rest = list
    .filter((item) => item.id !== featured.id)
    .sort((a, b) => (b.featureDay ?? 0) - (a.featureDay ?? 0));
  return [featured, ...rest].map((item) => ({
    ...item,
    isFeatured: item.id === featured.id,
    isPublished: item.isPublished !== false,
  }));
}

export function pickFeaturedLearning<T extends FeaturedFields>(
  items: T[] | null | undefined,
  journeyDay = 1
): T | undefined {
  const merged = presentSauceArticles(items, journeyDay);
  return merged.find((item) => item.isFeatured) || merged[0];
}

type SequencedFields = FeaturedFields & { sequence?: 'journey' | 'shared' | null };

export function usesSharedSequence(items: SequencedFields[] | null | undefined): boolean {
  return Array.isArray(items) && items.some((item) => item.sequence === 'shared');
}

/**
 * Default sequence ignores join date. Every published card stays visible,
 * and the highest day number is the featured card.
 */
export function withSharedSequence<T extends FeaturedFields>(items: T[] | null | undefined): T[] {
  const demo = DEMO_LEARNING_CONTENT[0] as unknown as T;
  const list = (Array.isArray(items) ? items : []).filter(
    (item) => item.id !== DEMO_ARTICLE_ID && item.isPublished !== false
  );
  const featured = pickSharedFeatured(list) || demo;
  const rest = list
    .filter((item) => item.id !== featured.id)
    .sort((a, b) => (b.featureDay ?? 0) - (a.featureDay ?? 0));
  return [featured, ...rest].map((item) => ({
    ...item,
    isFeatured: item.id === featured.id,
    isPublished: item.isPublished !== false,
  }));
}

export function presentSauceArticles<T extends SequencedFields>(
  items: T[] | null | undefined,
  journeyDay = 1
): T[] {
  return usesSharedSequence(items)
    ? withSharedSequence(items)
    : withBundledUltraArticle(items, journeyDay);
}
